import React, { useMemo, forwardRef, useImperativeHandle } from 'react';
import GearSpinner from '../GearSpinner/GearSpinner';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import moment from 'moment';

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
);

// 🔹 Plugin to draw labels on top of the specific background blocks
const backgroundLabelPlugin = {
    id: 'backgroundLabelPlugin',
    afterDatasetsDraw(chart) {
        const { ctx, data, scales: { y1 } } = chart;
        const meta = chart.getDatasetMeta(0); // 0 = Background Dataset

        if (!meta) return;

        ctx.save();
        ctx.font = 'bold 11px Arial';
        ctx.fillStyle = '#000';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'bottom';

        meta.data.forEach((bar, index) => {
            const rawValue = data.datasets[0].data[index];
            if (rawValue !== null && rawValue !== undefined) {
                const labelText = data.labels[index]?.bgLabel;
                if (labelText) {
                    // Draw text exactly on top of the bar
                    ctx.fillText(labelText, bar.x, y1.getPixelForValue(rawValue) - 5);
                }
            }
        });

        ctx.restore();
    }
};

const SalesBarChart = forwardRef(({ data, chatTitle }, ref) => {
    const chartRef = React.useRef(null);
    useImperativeHandle(ref, () => ({
        getImage: () => {
            if (!chartRef.current) return null;
            return chartRef.current.toBase64Image('image/png');
        }
    }));
    const { chartData, maxVal } = useMemo(() => {
        const result = data?.result || data;
        
        if (!result || !result.monthly_sales || result.monthly_sales.length === 0) {
            return { chartData: null };
        }

        const monthlySales = result.monthly_sales;
        const yearlyStats = result.yearly_sales || {};

        const yearsInView = [...new Set(monthlySales.map(m => m.sales_year))].sort();
        const firstYear = yearsInView[0]; 
        
        const yearPrev = firstYear - 1; 
        const yearCurr = firstYear;     

        const labels = [];
        const monthlyValues = [];
        const backgroundValues = [];

        monthlySales.forEach((item, index) => {
            let bgLabelText = '';
            let bgValue = null;

            if (index === 0) { 
                bgLabelText = yearPrev.toString(); 
                bgValue = (yearlyStats[yearPrev.toString()] || 0) / 1000;
            } 
            else if (index === 3) {
                bgLabelText = yearCurr.toString();
                bgValue = (yearlyStats[yearCurr.toString()] || 0) / 1000;
            }
            else if (index === 6) {
                bgLabelText = 'Previous 12 Months';
                bgValue = (yearlyStats.rolling_prev_12 || 0) / 1000;
            }
            else if (index === 9) {
                bgLabelText = 'Last 12 Months';
                bgValue = (yearlyStats.rolling_last_12 || 0) / 1000;
            }

            labels.push({
                label: moment(item.sales_month).format('MMM-YY'),
                bgLabel: bgLabelText 
            });

            monthlyValues.push(item.total_sales / 1000);
            backgroundValues.push(bgValue);
        });

        const maxMonthly = Math.max(...monthlyValues, 0);
        const validBgValues = backgroundValues.filter(v => v !== null);
        const maxAnnual = validBgValues.length > 0 ? Math.max(...validBgValues) : 0;

        return {
            chartData: {
                labels: labels,
                datasets: [
                    {
                        label: 'Annual Sales',
                        data: backgroundValues,
                        backgroundColor: '#dae8fc', 
                        hoverBackgroundColor: '#cce0ff', 
                        yAxisID: 'y1',
                        hoverBackgroundColor: '#bed7fa', 
                        order: 2, 
                        barThickness: 70, 
                        skipNull: true 
                    },
                    {
                        label: 'Monthly Sales',
                        data: monthlyValues,
                        backgroundColor: '#4472c4', 
                        hoverBackgroundColor: '#365a9e',
                        yAxisID: 'y', 
                        order: 1, 
                        barThickness: 12, 
                        borderRadius: 2,
                    }
                ]
            },
            maxVal: { monthly: maxMonthly, annual: maxAnnual }
        };
    }, [data]);

    if (!chartData) return <div className="p-4 text-center"><GearSpinner /></div>;

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
            mode: 'nearest', 
            axis: 'x',
            intersect: true 
        },
        plugins: {
            legend: { display: false },
            title: { display: true, text: `${chatTitle}`, font: { size: 16 }, color: '#000' },
            tooltip: {
                callbacks: {
                    // ⬇️ FIXED: Properly access the label object from data
                    title: (items) => {
                        const item = items[0];
                        const index = item.dataIndex;
                        const labelObj = item.chart.data.labels[index]; // Get the full object

                        // If hovering the Background bar (Dataset 0)
                        if (item.datasetIndex === 0) {
                            return labelObj.bgLabel || 'Annual';
                        }

                        // If hovering the Monthly bar (Dataset 1)
                        // Return the string label we stored (e.g. "Jan-24")
                        return labelObj.label; 
                    },
                    label: (ctx) => {
                        if (ctx.raw === null) return null;
                        let val = ctx.raw * 1000;
                        const prefix = ctx.datasetIndex === 0 ? 'Total: ' : 'Sales: ';
                        return `${prefix}$${val.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
                    }
                }
            }
        },
        scales: {
            x: {
                grid: { display: false },
                ticks: {
                    // ⬇️ Extract string label for X-axis ticks
                    callback: function(val, index) {
                         const labelObj = this.chart.data.labels[index];
                         return labelObj ? labelObj.label : '';
                    },
                    maxRotation: 90, minRotation: 90,
                    font: { size: 11 }
                }
            },
            y: {
                type: 'linear', display: true, position: 'left',
                title: { display: true, text: 'Monthly' },
                min: 0, suggestedMax: maxVal.monthly * 1.2,
                ticks: { callback: (val) => val.toLocaleString() }
            },
            y1: {
                type: 'linear', display: true, position: 'right',
                title: { display: true, text: 'Annual' },
                grid: { display: false },
                min: 0, suggestedMax: maxVal.annual * 1.2,
                ticks: { callback: (val) => val.toLocaleString() }
            }
        }
    };

    return (
        <div style={{ height: '450px', width: '100%', backgroundColor: '#fff' }}>
            <Bar data={chartData} options={options} plugins={[backgroundLabelPlugin]} ref={chartRef}/>
        </div>
    );
});

export default SalesBarChart;